import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api, DEMO, errMsg, supabase } from "@/lib/api";

const Ctx = createContext(null);
const DEMO_KEY = "ajobf_demo_session";

export function AuthProvider({ children }) {
  const [session, setSession] = useState(DEMO && localStorage.getItem(DEMO_KEY) ? { demo: true } : null);
  const [me, setMe] = useState(null);
  const [aal, setAal] = useState(DEMO ? { current: "aal2", next: "aal2" } : { current: null, next: null });
  const [loading, setLoading] = useState(!!supabase || (DEMO && !!session));
  const [error, setError] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);
  const requestVersion = useRef(0);
  const invalidateRequests = useCallback(() => { requestVersion.current++; }, []);

  const loadMe = useCallback(async () => {
    if (!supabase && !DEMO) return;
    const version = ++requestVersion.current;
    setLoading(true);
    try {
      const { data } = await api.get("/me", { timeout: 10000 });
      let assurance = null;
      if (supabase) {
        const { data: a } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        assurance = { current: a?.currentLevel, next: a?.nextLevel };
      }
      if (version !== requestVersion.current) return false;
      setMe(data);
      setError(null);
      setErrorStatus(null);
      if (assurance) setAal(assurance);
      return true;
    } catch (e) {
      if (version !== requestVersion.current) return false;
      setMe(null);
      setError(errMsg(e));
      setErrorStatus(e?.response?.status || 0);
      return false;
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (DEMO) {
      if (session) loadMe();
      return invalidateRequests;
    }
    if (!supabase) return;
    let active = true;
    supabase.auth.getSession().then(async ({ data }) => {
      if (!active) return;
      setSession(data.session);
      if (data.session) await loadMe();
      else setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s);
      if (s) setTimeout(loadMe, 0);
      else {
        invalidateRequests();
        setMe(null);
        setError(null);
        setLoading(false);
      }
    });
    return () => { active = false; invalidateRequests(); sub.subscription.unsubscribe(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadMe, invalidateRequests]);

  useEffect(() => {
    if (!session || !error || (errorStatus && errorStatus < 500)) return;
    let stopped = false, timer, delay = 5000, retrying = false;
    const schedule = () => { timer = setTimeout(retry, delay); };
    async function retry() {
      clearTimeout(timer);
      if (stopped || retrying) return;
      if (document.visibilityState === "hidden" || navigator.onLine === false) { schedule(); return; }
      retrying = true;
      const ok = await loadMe();
      retrying = false;
      if (!ok && !stopped) { delay = Math.min(delay * 2, 30000); schedule(); }
    }
    const visible = () => { if (document.visibilityState !== "hidden") retry(); };
    schedule();
    window.addEventListener("online", retry);
    document.addEventListener("visibilitychange", visible);
    return () => { stopped = true; clearTimeout(timer); window.removeEventListener("online", retry); document.removeEventListener("visibilitychange", visible); };
  }, [session, error, errorStatus, loadMe]);

  const enterDemo = async () => {
    localStorage.setItem(DEMO_KEY, "1");
    setSession({ demo: true });
    await loadMe();
  };

  const signOut = async () => {
    invalidateRequests();
    if (DEMO) localStorage.removeItem(DEMO_KEY);
    await supabase?.auth.signOut();
    setSession(null);
    setMe(null);
    setError(null);
    setErrorStatus(null);
    setLoading(false);
  };

  return (
    <Ctx.Provider value={{ session, me, aal, loading, error, errorStatus, reload: loadMe, signOut, enterDemo, demo: DEMO, configured: !!supabase || DEMO }}>
      {children}
    </Ctx.Provider>
  );
}

export const useAuth = () => useContext(Ctx);
